import { useMemo } from 'react';
import { hhmmss } from '../lib/hooks';
import { Block, Cell, Kv, Pill, Strip, Table, type KvRow, type Row, type Tone } from '../ui/Block';

type Level = 'none' | 'low' | 'medium' | 'high';
interface CfgGpu { name: string; perH: number; slots: number; slotsMeasured?: boolean; memoryGb?: number; sm?: string }
interface CfgGroup { name: string; perH: number; slots: number; gpus?: string[] }
interface SpeechMachinesCfg { enabled?: boolean; runpod?: boolean; maxPerH?: number; readyTimeoutS?: number; heartbeatTimeoutS?: number; retryS?: number; racers?: number; bootCapS?: number; gpus?: { name: string; speed: number }[]; most?: number; linesPerMin?: number }
export interface BalCfg {
  gpuTypes?: CfgGroup[]; gpus?: CfgGpu[]; maxFirstPerH?: number; stockMaxAgeS?: number; badGpuFailures?: number; badGpuWindowS?: number; badGpuCooldownS?: number;
  refusedCooldownS?: number; speechMachines?: SpeechMachinesCfg; dataCenters?: string[]; fallbackDataCenters?: string[];
}
interface BalPod { id: number; state: string; since?: number; createdAt?: number; gpu?: { name: string; perH: number }; speechOnly?: boolean; host?: 'runpod' | 'vast'; placed?: { gpu: string; dataCenter?: string | null } | null }
export interface BalSnap {
  now?: number; cfg?: BalCfg;
  stock?: Record<string, [Level, number]>;
  refusedUntil?: Record<string, number>; refusedStreak?: Record<string, number>;
  gpuBadUntil?: Record<string, number>; badUntil?: Record<string, number>; gpuFailures?: Record<string, number[]>;
  speechRates?: Record<string, number>;
  pods?: Record<string, BalPod>;
  vastSlow?: number; speechMachineRetryAt?: number | null; runpodRefusedAt?: number | null;
}
export interface VastFilters { enabled: boolean; image: string; diskGb: number; minGpuRamGb: number; minReliability: number; minCuda: number; minInetDownMbps: number; minCpuCores: number; minRamGb: number; bootGb: number; bootWeight?: number; badHostH?: number; excludeCountries: string[] }
export interface PoolStatus { settings?: { balancer?: BalCfg; vast?: VastFilters }; balancer: BalSnap | null; vastConfigured?: boolean; speechMachines?: boolean }

const VAST_GRACE_S = 90;
const SPEECH_TIER = 9;
const LEVEL_TONE: Record<Level, Tone> = { high: 'ok', medium: 'info', low: 'warn', none: 'bad' };
const usd = (v: number | null | undefined, d = 2) => (v == null ? '–' : `$${v.toFixed(d)}`);
const key = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '').replace(/^nvidia/, '').replace(/^geforce/, '');
const span = (s: number) => (s < 90 ? `${Math.round(s)} s` : s < 5400 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`);
const tierName = (t: string) => (t === '0' ? 'near data centres' : t === '1' ? 'fallback data centres' : t === '2' ? 'anywhere' : t === String(SPEECH_TIER) ? 'speech' : `tier ${t}`);
const ago = (at: number, now: number) => `${span(Math.max(0, now - at))} ago`;
const clock = (unix: number) => hhmmss(unix * 1000);

function splitKey(k: string): [string, string] {
  const i = k.lastIndexOf('@');
  return i < 0 ? [k, ''] : [k.slice(0, i), k.slice(i + 1)];
}

export function GpuKnown({ st, runpodPerH, mobile, asOf }: { st: PoolStatus | null; runpodPerH: Record<string, number | null>; mobile: boolean; asOf: string }) {
  const b = st?.balancer ?? null;
  const cfg: BalCfg = { ...(st?.settings?.balancer ?? {}), ...(b?.cfg ?? {}) };
  const now = b?.now ?? Date.now() / 1000;
  const cap = cfg.maxFirstPerH ?? null;
  const maxAge = cfg.stockMaxAgeS ?? 900;
  const sm = cfg.speechMachines ?? {};
  const assumed = sm.linesPerMin ?? 200;
  const groups = cfg.gpuTypes ?? [];
  const rates = b?.speechRates ?? {};
  const rateOf = (n: string) => Object.entries(rates).find(([g]) => key(g) === key(n))?.[1] ?? null;

  const rows: Row[] = useMemo(() => {
    const stock = b?.stock ?? {};
    const refused = b?.refusedUntil ?? {};
    const streak = b?.refusedStreak ?? {};
    const failures = b?.gpuFailures ?? {};
    const window = cfg.badGpuWindowS ?? 3600;
    const catalog = cfg.gpus ?? [];
    const inGroup = (n: string) => groups.filter((g) => (g.gpus?.length ? g.gpus : [g.name]).includes(n));
    const speech = (n: string) => (sm.gpus ?? []).find((g) => key(g.name) === key(n));
    const known = new Set<string>([
      ...Object.keys(stock).map((k) => splitKey(k)[0]), ...Object.keys(b?.gpuBadUntil ?? {}), ...Object.keys(failures), ...Object.keys(rates),
    ]);
    const names = catalog.filter((g) => inGroup(g.name).length || speech(g.name) || known.has(g.name)).map((g) => g.name);
    for (const n of known) if (!names.includes(n) && catalog.some((g) => key(g.name) === key(n)) === false && n !== 'Vast') names.push(n);
    const rank = (n: string) => (inGroup(n).length ? 0 : speech(n) ? 1 : 2);
    names.sort((x, y) => rank(x) - rank(y) || (catalog.find((g) => g.name === x)?.perH ?? 99) - (catalog.find((g) => g.name === y)?.perH ?? 99));
    return names.map((n) => {
      const g = catalog.find((c) => c.name === n);
      const gs = inGroup(n);
      const sp = speech(n);
      const perH = g?.perH ?? null;
      const live = runpodPerH[n];
      const under = perH != null && cap != null ? perH <= cap + 1e-9 : null;
      const all = stock[`${n}@`];
      const fresh = (at: number) => now - at <= maxAge;
      const dcs = Object.entries(stock).filter(([k]) => splitKey(k)[0] === n && splitKey(k)[1]).map(([k, [lv, at]]) => ({ dc: splitKey(k)[1], lv, at }));
      const asks = [n, ...gs.map((x) => x.name)];
      const ref = Object.entries(refused).filter(([k, t]) => asks.includes(splitKey(k)[0]) && t > now).map(([k, t]) => ({ ask: splitKey(k)[0], tier: splitKey(k)[1], t }));
      const str = Math.max(0, ...asks.map((a) => streak[a] ?? 0));
      const badT = b?.gpuBadUntil?.[n];
      const bad = badT != null && badT > now;
      const groupBad = gs.map((x) => ({ g: x.name, t: b?.badUntil?.[x.name] })).filter((x) => x.t != null && x.t > now);
      const fails = (failures[n] ?? []).filter((t) => now - t <= window);
      const measured = rateOf(n);
      return {
        key: n,
        cells: [
          { t: n, sub: gs.length ? `in ${gs.map((x) => x.name).join(', ')}` : sp ? 'speech choice only' : 'not in the pool' },
          { t: usd(perH), mono: true, sub: live != null && perH != null && Math.abs(live - perH) > 0.005 ? `RunPod now ${usd(live)}` : under == null ? undefined : under ? `at or under the ${usd(cap)} cap` : `over the ${usd(cap)} cap`, fg: under === false ? 'var(--conf-mid)' : undefined },
          { t: g ? String(g.slots) : '–', mono: true, sub: g ? (g.slotsMeasured ? 'measured' : 'estimate') : undefined },
          all ? { pill: { label: fresh(all[1]) ? all[0] : `${all[0]} (old)`, tone: fresh(all[1]) ? LEVEL_TONE[all[0]] : 'mute' }, sub: ago(all[1], now) } : { t: 'not read', fg: 'var(--ink-55)' },
          { t: dcs.length ? <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>{dcs.map((d) => <Pill key={d.dc} tone={fresh(d.at) ? LEVEL_TONE[d.lv] : 'mute'} title={`${d.dc}: ${d.lv}, ${ago(d.at, now)}`}>{d.dc} {d.lv}</Pill>)}</span> : '–' },
          ref.length
            ? { t: `not asked for ${span(Math.max(...ref.map((r) => r.t)) - now)}`, sub: ref.map((r) => `${r.ask === n ? '' : `${r.ask}, `}${tierName(r.tier)} until ${clock(r.t)}`).join(' · ') + (str ? ` · refused ${str} in a row` : ''), fg: 'var(--conf-mid)' }
            : { t: str ? `refused ${str} in a row` : 'no refusal', fg: str ? 'var(--conf-mid)' : 'var(--ink-55)' },
          bad || groupBad.length
            ? { t: bad ? `set aside until ${clock(badT!)}` : `${groupBad[0].g} set aside until ${clock(groupBad[0].t!)}`, sub: `${fails.length} failed start${fails.length === 1 ? '' : 's'} in the last ${span(window)}${fails.length ? `, last ${ago(Math.max(...fails), now)}` : ''} (${cfg.badGpuFailures ?? 2} set a type aside)`, fg: 'var(--conf-low)' }
            : { t: fails.length ? `${fails.length} failed start${fails.length === 1 ? '' : 's'}` : 'healthy', sub: fails.length ? `in the last ${span(window)}, last ${ago(Math.max(...fails), now)}; ${cfg.badGpuFailures ?? 2} set it aside` : undefined, fg: fails.length ? 'var(--conf-mid)' : 'var(--ink-55)' },
          measured != null
            ? { t: `${Math.round(measured)} lines/min`, sub: `measured on a full speech machine${sp ? ` · speed ${sp.speed}` : ''}`, mono: true, fg: 'var(--ok)' }
            : { t: 'not measured yet', sub: `assumed ${Math.round(assumed)} lines/min${sp ? ` · speed ${sp.speed}` : ''}`, fg: 'var(--ink-55)' },
        ],
      };
    });
  }, [b, cfg, groups, sm, now, cap, maxAge, assumed, runpodPerH]); // eslint-disable-line react-hooks/exhaustive-deps

  const asks: Row[] = Object.entries(b?.refusedUntil ?? {}).filter(([, t]) => t > now).sort((x, y) => y[1] - x[1]).map(([k, t]) => {
    const [ask, tier] = splitKey(k);
    return { key: k, cells: [{ t: ask }, { t: tierName(tier) }, { t: clock(t), sub: `in ${span(t - now)}`, mono: true }, { t: String(b?.refusedStreak?.[ask] ?? 0), mono: true }] };
  });

  const otherRates = Object.entries(rates).filter(([g]) => !(cfg.gpus ?? []).some((c) => key(c.name) === key(g)));
  const machines = Object.values(b?.pods ?? {}).filter((p) => p.speechOnly && !['gone', 'stopped'].includes(p.state));
  const racers = Math.max(1, Math.min(5, sm.racers ?? 3));
  const bootCap = (sm.bootCapS ?? 150) > 0 ? Math.min((sm.bootCapS ?? 150) * (1 + Math.floor((b?.vastSlow ?? 0) / racers)), sm.readyTimeoutS ?? 900) : (sm.readyTimeoutS ?? 900);
  const retry = b?.speechMachineRetryAt != null && b.speechMachineRetryAt > now ? b.speechMachineRetryAt : null;
  const v = st?.settings?.vast;

  const machineRows: Row[] = machines.map((p) => {
    const vast = p.host === 'vast';
    const coming = p.state === 'creating' || p.state === 'booting';
    const age = now - (p.createdAt ?? now);
    const gpu = p.placed?.gpu ?? null;
    const r = gpu ? rateOf(gpu) : null;
    return {
      key: String(p.id),
      cells: [
        { t: `${vast ? 'Vast' : 'RunPod'} ${p.id}`, sub: vast ? 'speech machine' : 'speech-only pod' },
        { pill: { label: p.state, tone: p.state === 'ready' ? 'ok' : coming ? 'info' : 'mute' }, sub: coming ? `${span(age)} since asked${vast && age > VAST_GRACE_S ? ' · past the 90 s grace: RunPod may race' : ''}` : undefined },
        { t: gpu ?? '–', sub: p.placed?.dataCenter ?? undefined },
        { t: usd(p.gpu?.perH), mono: true },
        { t: r != null ? `${Math.round(r)} lines/min` : `assumed ${Math.round(assumed)}`, mono: true, fg: r != null ? 'var(--ok)' : 'var(--ink-55)' },
      ],
    };
  });

  const vastRows: KvRow[] = v ? [
    { k: 'Vast speech machines', v: { t: v.enabled ? (st?.speechMachines ? 'on' : st?.vastConfigured ? 'off (the pool is not racing)' : 'on, but no VAST_API_KEY') : 'off', fg: v.enabled && st?.speechMachines ? 'var(--ok)' : 'var(--conf-mid)' } },
    { k: 'Price, all in', v: { t: `at most ${usd(sm.maxPerH ?? 0.6)}/h`, mono: true } },
    { k: 'GPU memory', v: { t: `${v.minGpuRamGb} GB or more`, mono: true } },
    { k: 'Host reliability', v: { t: `${v.minReliability} or more`, mono: true } },
    { k: 'CUDA', v: { t: `${v.minCuda} or newer`, mono: true } },
    { k: 'Download', v: { t: `${v.minInetDownMbps} Mbps or more`, mono: true } },
    { k: 'CPU and memory', v: { t: `${v.minCpuCores} cores, ${v.minRamGb} GB or more`, mono: true } },
    { k: 'Disk', v: { t: `${v.diskGb} GB (image pull ~${v.bootGb} GB)`, mono: true } },
    { k: 'Boot weight', v: { t: v.bootWeight != null ? `${v.bootWeight} $/h per minute of estimated pull` : 'default', mono: true } },
    { k: 'A bad host is skipped for', v: { t: v.badHostH != null ? `${v.badHostH} h` : 'default', mono: true } },
    { k: 'Countries left out', v: { t: v.excludeCountries.join(', ') || 'none', mono: true } },
    { k: 'Image', v: { t: v.image || '–', mono: true } },
  ] : [];

  const raceRows: KvRow[] = [
    { k: 'Racers', v: { t: `${racers} at once; the first ready speaks, the rest are destroyed`, mono: false } },
    { k: 'Boot cap now', v: { t: span(bootCap), mono: true, fg: (b?.vastSlow ?? 0) >= racers ? 'var(--conf-mid)' : undefined }, kSub: `${span(sm.bootCapS ?? 150)} set, doubles while every host is slow (${b?.vastSlow ?? 0} slow so far)` },
    { k: 'RunPod speech-only pods', v: { t: sm.runpod ? `on: when Vast refuses or is not ready after ${VAST_GRACE_S} s` : 'off', fg: sm.runpod ? undefined : 'var(--ink-55)' } },
    { k: 'Most speech machines', v: { t: String(sm.most ?? 10), mono: true }, kSub: 'a second starts when those up make over 80% of the lines a minute they can' },
    { k: 'Vast asked again', v: { t: retry ? `in ${span(retry - now)} (${clock(retry)})` : 'now, if a voice is wanted', fg: retry ? 'var(--conf-mid)' : undefined }, kSub: `after a refusal or a lost machine, ${span(sm.retryS ?? 120)}` },
    { k: 'RunPod last refused', v: { t: b?.runpodRefusedAt ? `${clock(b.runpodRefusedAt)} (${ago(b.runpodRefusedAt, now)})` : 'not lately', mono: true } },
    { k: 'Speech GPU choices', v: { t: (sm.gpus ?? []).map((g) => `${g.name} ×${g.speed}`).join(' · ') || '–' }, kSub: `RunPod speech-only pods: the most voice per dollar first, at or under ${usd(cap)}/h` },
  ];

  return (
    <>
      <Cell span={12}>
        <Block title="What the balancer knows, by type" asOf={asOf}
          sub={`From the balancer's own state (its clock ${b?.now ? clock(b.now) : '–'}): the $/h it uses and whether a first pod may take it, RunPod's stock overall and per data centre (older than ${span(maxAge)} is ignored), asks RunPod refused, types set aside after failed starts, and the voice lines a minute measured on full speech machines.`}
          empty={!b ? 'The balancer has not run in this environment yet.' : !rows.length ? 'No GPU type known.' : undefined}>
          {b && (
            <Strip items={[
              { k: 'FIRST POD CAP', v: cap != null ? `${usd(cap)}/h` : '–' },
              { k: 'UNDER THE CAP IN STOCK', v: String((cfg.gpus ?? []).filter((g) => cap != null && g.perH <= cap + 1e-9 && b.stock?.[`${g.name}@`] && b.stock[`${g.name}@`][0] !== 'none' && now - b.stock[`${g.name}@`][1] <= maxAge).length) },
              { k: 'REFUSED ASKS', v: String(asks.length), fg: asks.length ? 'var(--conf-mid)' : undefined },
              { k: 'SET ASIDE', v: String(Object.values(b.gpuBadUntil ?? {}).filter((t) => t > now).length), fg: Object.values(b.gpuBadUntil ?? {}).some((t) => t > now) ? 'var(--conf-low)' : undefined },
              { k: 'SPEECH MEASURED', v: Object.keys(rates).length ? Object.entries(rates).map(([g, r]) => `${g} ${Math.round(r)}/min`).join(' · ') : `none yet, assumed ${Math.round(assumed)}/min` },
            ]} />
          )}
          {rows.length > 0 && (
            <Table mobile={mobile} minWidth={1300} rows={rows} cols={[
              { label: 'Type', width: 'minmax(150px, 1fr)' }, { label: 'Balancer $/h', width: '150px' }, { label: 'Slots', width: '80px' },
              { label: 'RunPod stock', width: '110px' }, { label: 'By data centre', width: 'minmax(200px, 1.4fr)' }, { label: 'Refused', width: 'minmax(170px, 1fr)' },
              { label: 'Failed starts', width: 'minmax(170px, 1fr)' }, { label: 'Voice lines a minute', width: '170px' },
            ]} />
          )}
        </Block>
      </Cell>
      {asks.length > 0 && (
        <Cell span={12}>
          <Block title="Asks RunPod refused" count={asks.length} sub="Each ask (a type, a group or the speech GPU ask) is not sent there again until the time shown; several refusals in a row wait longer.">
            <Table mobile={mobile} minWidth={640} rows={asks} cols={[{ label: 'Ask', width: 'minmax(160px, 1fr)' }, { label: 'Where', width: '180px' }, { label: 'Not asked until', width: '150px' }, { label: 'In a row', width: '90px' }]} />
          </Block>
        </Cell>
      )}
      <Cell span={mobile ? 12 : 6}>
        <Block title="Vast: the machines it may rent" sub="Voices only (readings, clones, previews, background sound, live voices); never a transcription. The offer filters from this environment's gpu_pool setting." empty={!v ? 'No Vast settings saved.' : undefined}>
          {v && <Kv rows={vastRows} />}
        </Block>
      </Cell>
      <Cell span={mobile ? 12 : 6}>
        <Block title="Speech machines: the race" sub="A wanted voice races Vast machines first; RunPod speech-only pods only when Vast refused or none is ready in time.">
          <Kv rows={raceRows} />
          {otherRates.length > 0 && <div style={{ padding: '0 14px 12px', fontSize: 12, color: 'var(--ink-62)' }}>Measured on other GPUs: {otherRates.map(([g, r]) => `${g} ${Math.round(r)} lines/min`).join(' · ')}</div>}
        </Block>
      </Cell>
      <Cell span={12}>
        <Block title="Speech machines now" count={machineRows.length} empty={b && !machineRows.length ? 'No speech machine is up or coming.' : undefined} emptyGood>
          {machineRows.length > 0 && (
            <Table mobile={mobile} minWidth={760} rows={machineRows} cols={[{ label: 'Machine', width: 'minmax(140px, 1fr)' }, { label: 'State', width: '220px' }, { label: 'GPU', width: '150px' }, { label: '$/h', width: '80px' }, { label: 'Can make', width: '140px' }]} />
          )}
        </Block>
      </Cell>
    </>
  );
}
